import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, jsonBody } from "../../api/client";
import type { ListResponse, Location } from "../../api/types";
import { useWorkspaceLens, WorkspacePointer } from "../../app/workspaceLens";
import { titleCase } from "../../lib/format";
import { Card, EditorPage, EmptyState, ErrorPanel, Field, FormActions } from "../../ui/components";
import { EditorPageSkeleton } from "../../ui/skeletons";
import { useVenues } from "./queries";

function notes(value: unknown): string {
  if (!value || typeof value !== "object") return "";
  const item = (value as Record<string, unknown>).notes;
  return typeof item === "string" ? item : "";
}

export function LocationEditorPage() {
  const { venueId = "", locationId } = useParams<{ venueId: string; locationId?: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const lens = useWorkspaceLens();
  // Hire settings are lettings work: how long a booking may run and whether the public may make
  // one only mean something when the space is let to somebody else. The events side is not asked
  // about them, and whatever it cannot see is carried through the save unchanged.
  const hire = lens.isBookings;
  const venues = useVenues();
  const venue = venues.data?.items.find((item) => item.id === venueId);
  const locations = useQuery({
    queryKey: ["venues", venueId, "locations"],
    queryFn: () => api<ListResponse<Location>>(`/api/v1/venues/${venueId}/locations`),
    enabled: Boolean(venue),
  });
  const value = locationId
    ? locations.data?.items.find((item) => item.id === locationId)
    : undefined;
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();
  const backTo = `/venues?venue=${encodeURIComponent(venueId)}`;

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!venue) return;
    const form = new FormData(event.currentTarget);
    const capacity = String(form.get("capacity") ?? "");
    const maximum = String(form.get("maximumMinutes") ?? "");
    const parent = String(form.get("parentLocationId") ?? "");
    const payload = {
      parentLocationId: parent || null,
      name: String(form.get("name")),
      code: String(form.get("code")),
      kind: String(form.get("kind")),
      description: String(form.get("description")),
      capacity: capacity ? Number(capacity) : null,
      accessibility: { notes: String(form.get("accessibility")) },
      facilities: { notes: String(form.get("facilities")) },
      bufferBeforeMinutes: Number(form.get("bufferBeforeMinutes")),
      bufferAfterMinutes: Number(form.get("bufferAfterMinutes")),
      minimumMinutes: hire ? Number(form.get("minimumMinutes")) : (value?.minimumMinutes ?? 30),
      maximumMinutes: hire ? (maximum ? Number(maximum) : null) : (value?.maximumMinutes ?? null),
      isBookable: form.get("isBookable") === "on",
      publicBookingEnabled: hire
        ? form.get("publicBookingEnabled") === "on"
        : (value?.publicBookingEnabled ?? false),
      status: String(form.get("status")),
    };
    setWorking(true);
    setError(undefined);
    try {
      await api<Location>(
        value
          ? `/api/v1/venues/${venue.id}/locations/${value.id}`
          : `/api/v1/venues/${venue.id}/locations`,
        { method: value ? "PUT" : "POST", body: jsonBody(payload) },
      );
      await queryClient.invalidateQueries({ queryKey: ["venues", venue.id, "locations"] });
      navigate(backTo);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  if (venues.isLoading || (venue && locations.isLoading)) return <EditorPageSkeleton />;
  if (venues.error || locations.error)
    return <ErrorPanel error={venues.error ?? locations.error} />;
  const missing = !venue || (Boolean(locationId) && !value);

  return (
    <EditorPage
      title={value ? "Edit space" : "Add space"}
      description={venue?.name ?? "Space editor"}
      backTo={backTo}
      backLabel="Back to venue"
    >
      {missing ? (
        <EmptyState
          title="Space not found"
          description="The venue or space is outside your current access scope or no longer exists."
        />
      ) : (
        <Card>
          {error ? <ErrorPanel error={error} /> : null}
          <form className="form-grid" onSubmit={submit} key={value?.id ?? "new-location"}>
            <Field label="Name">
              <input name="name" required defaultValue={value?.name} />
            </Field>
            <Field label="Code">
              <input name="code" required defaultValue={value?.code} />
            </Field>
            <Field label="Parent space" hint="Optional hierarchy for areas inside another space.">
              <select name="parentLocationId" defaultValue={value?.parentLocationId ?? ""}>
                <option value="">No parent</option>
                {locations.data?.items
                  .filter((item) => item.id !== value?.id)
                  .map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
              </select>
            </Field>
            <Field label="Type">
              <select name="kind" defaultValue={value?.kind ?? "room"}>
                {[
                  "room",
                  "hall",
                  "field",
                  "court",
                  "stage",
                  "workspace",
                  "parking",
                  "equipment_area",
                  "other",
                ].map((kind) => (
                  <option key={kind} value={kind}>
                    {titleCase(kind)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Capacity">
              <input name="capacity" type="number" min={0} defaultValue={value?.capacity} />
            </Field>
            <Field label="Status">
              <select name="status" defaultValue={value?.status ?? "active"}>
                <option value="active">Active</option>
                <option value="maintenance">Maintenance</option>
                <option value="inactive">Inactive</option>
                <option value="archived">Archived</option>
              </select>
            </Field>
            <Field
              label="Changeover before (minutes)"
              hint="Kept free in the calendar before anything scheduled here, whether an event or a hire."
            >
              <input
                name="bufferBeforeMinutes"
                type="number"
                min={0}
                defaultValue={value?.bufferBeforeMinutes ?? 0}
                required
              />
            </Field>
            <Field
              label="Changeover after (minutes)"
              hint="Kept free afterwards for teardown and cleaning."
            >
              <input
                name="bufferAfterMinutes"
                type="number"
                min={0}
                defaultValue={value?.bufferAfterMinutes ?? 0}
                required
              />
            </Field>
            <Field label="Description">
              <textarea name="description" defaultValue={value?.description} />
            </Field>
            <Field label="Accessibility details">
              <textarea name="accessibility" defaultValue={notes(value?.accessibility)} />
            </Field>
            <Field label="Facilities">
              <textarea name="facilities" defaultValue={notes(value?.facilities)} />
            </Field>
            <div className="stack">
              <label className="check-field">
                <input
                  name="isBookable"
                  type="checkbox"
                  defaultChecked={value?.isBookable ?? true}
                />{" "}
                Staff can schedule this space
              </label>
            </div>
            {hire ? (
              <fieldset className="full">
                <legend>Hire settings</legend>
                <div className="form-grid">
                  <Field label="Shortest booking (minutes)">
                    <input
                      name="minimumMinutes"
                      type="number"
                      min={1}
                      defaultValue={value?.minimumMinutes ?? 30}
                      required
                    />
                  </Field>
                  <Field label="Longest booking (minutes)" hint="Leave empty for no limit.">
                    <input
                      name="maximumMinutes"
                      type="number"
                      min={1}
                      defaultValue={value?.maximumMinutes}
                    />
                  </Field>
                  <label className="check-field">
                    <input
                      name="publicBookingEnabled"
                      type="checkbox"
                      defaultChecked={value?.publicBookingEnabled ?? false}
                    />{" "}
                    Eligible for public booking
                  </label>
                </div>
              </fieldset>
            ) : (
              <div className="full">
                <WorkspacePointer target="bookings">
                  Booking lengths, prices, and public booking eligibility for this space are set in
                  the Booking workspace.
                </WorkspacePointer>
              </div>
            )}
            <div className="full">
              <FormActions
                submitting={working}
                submitLabel={value ? "Save space" : "Add space"}
                onCancel={() => navigate(backTo)}
              />
            </div>
          </form>
        </Card>
      )}
    </EditorPage>
  );
}
